import { HStack, IconButton, StackProps } from "@chakra-ui/react"
import { BlockLayoutType, ServerBlockTypeType } from "@pathwright/blocks-core"
import {
  BlockType,
  EDITOR_LAYERING,
  useEditorStateSelector
} from "@pathwright/blocks-engine"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import styled from "styled-components"
import CopyLinkButton from "../block/CopyBlockLink"
import BlockLayoutSwitcher from "./BlockSelector/BlockLayoutSwitcher"
import BlockStylesMenu from "./BlockStyles/BlockStylesMenu"

const BlockControlsWrap = styled.div`
  width: calc(100% - 10px);
  height: 30px;
  position: absolute;
  top: 5px;
  left: 5px;
  z-index: ${EDITOR_LAYERING.CONTROLS};
  display: flex;
  gap: 0;
  align-items: center;
  justify-content: space-between;
  pointer-events: none;

  .OverlayTrigger {
    cursor: pointer;
  }

  .Tooltip__trigger.OverlayTrigger .Overlay {
    height: 0;
    width: 0;
    position: relative;

    & > div {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
    }
  }

  .chakra-button[aria-label] {
    min-width: 0;
    cursor: pointer;
    background-color: transparent;
    padding: 0;
    width: 1em;
    min-width: 1em;
    margin: 0;
    height: auto;

    &:hover {
      background-color: transparent;
    }
  }

  button > svg {
    font-size: unset;
  }
`

const ControlsWrapper = styled.div`
  display: flex;
  align-items: center;
`

const Controls = (props: StackProps) => {
  return (
    <HStack
      color="#666"
      bgColor="#efefef"
      borderRadius="card"
      p={2}
      py="5px"
      mx={1}
      minH="31.6px"
      width="auto"
      alignItems="center"
      justifyContent="center"
      pointerEvents="all"
      fontSize="0.9em"
      gap={4}
      {...props}
    />
  )
}

type BlockControlsProps = {
  onDelete: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onCopy: () => void
  blockType: ServerBlockTypeType
  block: BlockType
  blockIndex: number
  onSelectLayout: (layout: BlockLayoutType["key"]) => void
}

const BlockControls = (props: BlockControlsProps) => {
  const {
    onDelete,
    onMoveUp,
    onMoveDown,
    onCopy,
    blockType,
    block,
    blockIndex,
    onSelectLayout
  } = props

  const { layout } = block
  const { layouts } = blockType

  const onChangeBlockStyles = useEditorStateSelector(
    (state) => state.handleUpdateBlockStyles
  )

  const handleUpdateBlockStyles = (styles: Record<string, any>) => {
    onChangeBlockStyles(block, styles)
  }

  const helpLink =
    layouts[layout] && layouts[layout].helpLink
      ? layouts[layout].helpLink
      : blockType.helpLink

  return (
    <BlockControlsWrap className="BlockControls">
      <ControlsWrapper className="BlockControls__display-controls">
        <Controls>
          <BlockLayoutSwitcher block={block} onSelectLayout={onSelectLayout} />
          <BlockStylesMenu
            currentStyles={block.style}
            onChangeStyles={handleUpdateBlockStyles}
          />
        </Controls>
      </ControlsWrapper>
      <Controls className="BlockControls__action-controls">
        <Tooltip title="Move block up one position">
          <IconButton
            children={<Pathicon icon="arrow-up" />}
            color="#666"
            onClick={onMoveUp}
            aria-label="Move block up one position"
          />
        </Tooltip>
        <Tooltip title="Move block down one position">
          <IconButton
            children={<Pathicon icon="arrow-down" />}
            color="#666"
            onClick={onMoveDown}
            aria-label="Move block down one position"
          />
        </Tooltip>
        <Tooltip title="Copy this block">
          <IconButton
            aria-label="Copy this block"
            children={<Pathicon icon="copy" />}
            color="#666"
            onClick={onCopy}
          />
        </Tooltip>
        <Tooltip title="Delete this block">
          <IconButton
            aria-label="Delete this block"
            children={<Pathicon icon="trash" />}
            color="#666"
            onClick={onDelete}
          />
        </Tooltip>
        <Tooltip title="Get a link to this block">
          <CopyLinkButton block={block} />
        </Tooltip>
        {helpLink ? (
          <Tooltip title="Get tips for using this block">
            <IconButton
              asChild
              aria-label="Get tips for using this block"
              color="#666"
            >
              <Link
                href={helpLink}
                target="_blank"
                // Having to finagle the overall icon size due to the HTML structure in this link case.
                fontSize=".9em"
              >
                <Pathicon icon="info-circle" />
              </Link>
            </IconButton>
          </Tooltip>
        ) : null}
      </Controls>
    </BlockControlsWrap>
  )
}

export default BlockControls
